import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:6555');
const FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt,.zip,.7z,.psd,.ai,.cdr,.mp4,.mov';

function fileName(file) {
  if (!file) return 'file';
  if (typeof file === 'string') return file.split('/').pop();
  return file.name || file.filename || 'file';
}

function fileType(file) {
  return typeof file === 'object' ? (file.content_type || '') : '';
}

function isPreviewable(file) {
  const name = fileName(file);
  const type = fileType(file);
  return /^image\//.test(type) || type === 'application/pdf' || /\.(jpg|jpeg|png|gif|webp|pdf)$/i.test(name);
}

const STATUS_LABELS = { new: 'Новая', in_progress: 'В работе', closed: 'Закрыта' };
const PRIORITY_LABEL = { urgent: '🔴 Срочно', normal: '🟡 Обычно', low: '🟢 Не горит' };
const BLOCK_NAMES = {
  tech: '🔧 Тех. работы', design: '🎨 Дизайн', video: '🎬 Видео',
  loyalty: '🎁 Лояльность', social: '📱 Контент', novelties: '🆕 Новинки',
};

function ticketPointsText(ticket) {
  const points = Array.isArray(ticket?.points) && ticket.points.length
    ? ticket.points
    : ticket?.point ? [ticket.point] : [];
  return points.join(', ') || 'Не указано';
}

export default function AppTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const messagesEndRef = useRef();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [msgText, setMsgText] = useState('');
  const [msgFiles, setMsgFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadTicket = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setLoadError('');
    try {
      const response = await api.get(`/api/app/tickets/${id}`);
      setTicket(response.data);
    } catch {
      if (!silent) setLoadError('Не удалось загрузить заявку');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') loadTicket({ silent: true });
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    const pollId = window.setInterval(refreshWhenVisible, 25000);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.clearInterval(pollId);
    };
  }, [loadTicket]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [ticket?.messages]);

  const closePreview = () => {
    if (preview?.objectUrl) URL.revokeObjectURL(preview.objectUrl);
    setPreview(null);
  };

  const openFile = async (file) => {
    setActionError('');
    try {
      if (typeof file === 'string') {
        const fullUrl = file.startsWith('http') ? file : `${BASE_URL}${file}`;
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (!isPreviewable(file)) {
        const res = await api.get(`/api/files/${file.id}/link`);
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
        return;
      }

      const res = await api.get(`/api/files/${file.id}/inline`, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(res.data);
      setPreview({
        objectUrl,
        name: fileName(file),
        type: file.content_type || res.data.type || '',
      });
    } catch {
      setActionError('Не удалось открыть файл. Попробуйте ещё раз.');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setActionError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('ticket_id', id);
      const res = await api.post('/api/app/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsgFiles(prev => [...prev, res.data]);
    } catch {
      setActionError('Не удалось загрузить файл. Попробуйте ещё раз.');
    } finally {
      setUploading(false);
      fileRef.current.value = '';
    }
  };

  const sendMessage = async () => {
    if (!msgText.trim() && msgFiles.length === 0) return;
    setSending(true);
    setActionError('');
    try {
      const res = await api.post(`/api/app/tickets/${id}/message`, { content: msgText, files: msgFiles });
      setTicket(prev => ({ ...prev, messages: [...(prev.messages || []), res.data] }));
      setMsgText('');
      setMsgFiles([]);
    } catch {
      setActionError('Не удалось отправить сообщение. Попробуйте ещё раз.');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="app-loader"><div className="spinner" /><p>Загрузка...</p></div>;

  if (loadError) return (
    <div className="app-page centered">
      <div className="error-msg">{loadError}</div>
      <button className="btn-primary" onClick={() => loadTicket()}>Повторить</button>
      <button className="btn-secondary" onClick={() => navigate('/app')}>← Назад</button>
    </div>
  );

  return (
    <div className="app-page ticket-page">
      {preview && <FilePreviewModal preview={preview} onClose={closePreview} />}
      <div className="app-header">
        <button className="btn-back" onClick={() => navigate('/app')}>←</button>
        <div>
          <div className="ticket-number-small">{ticket.ticket_number}</div>
          <span className={`status-badge status--${ticket.status}`}>{STATUS_LABELS[ticket.status]}</span>
        </div>
      </div>

      <div className="ticket-info-compact">
        <div className="info-row-compact"><span>{BLOCK_NAMES[ticket.block]}</span><span>·</span><span>{ticket.problem_type}</span></div>
        <div className="info-row-compact"><span>📍 {ticketPointsText(ticket)}</span></div>
        <div className="info-row-compact"><span>{PRIORITY_LABEL[ticket.priority]}</span></div>
        {ticket.deadline && <div className="info-row-compact"><span>📅 {new Date(ticket.deadline).toLocaleString('ru-RU')}</span></div>}
      </div>

      <div className="description-card">
        <p>{ticket.description}</p>
      </div>

      {ticket.files?.length > 0 && (
        <div className="files-preview">
          {ticket.files.map((f, i) => (
            <SecureFilePreview key={i} file={f} onOpen={openFile} />
          ))}
        </div>
      )}

      <div className="messages-section">
        <h3>Переписка</h3>
        {actionError && <div className="error-msg" role="alert">{actionError}</div>}
        <div className="messages-list">
          {(ticket.messages || []).length === 0 ? (
            <div className="empty-state small">Сообщений пока нет</div>
          ) : (
            ticket.messages.map(m => (
              <div key={m.id} className={`message-bubble ${m.user?.id !== ticket.user?.id ? 'message-incoming' : 'message-own'}`}>
                <div className="message-author">{m.user?.id !== ticket.user?.id ? (m.user?.full_name || 'Сотрудник поддержки') : 'Вы'}</div>
                <div className="message-content">{m.content}</div>
                {m.files?.map((f, i) => <SecureFilePreview key={i} file={f} onOpen={openFile} />)}
                <div className="message-time">{new Date(m.created_at).toLocaleString('ru-RU')}</div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {ticket.status !== 'closed' && (
          <div className="message-input-area">
            <textarea
              placeholder="Написать сообщение..."
              value={msgText}
              onChange={e => setMsgText(e.target.value)}
              rows={3}
            />
            {msgFiles.length > 0 && (
              <div className="attached-files">
                {msgFiles.map((f, i) => (
                  <span key={i} className="attached-file">
                    📎 {fileName(f)}
                    <button onClick={() => setMsgFiles(prev => prev.filter((_, j) => j !== i))}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <div className="message-actions">
              <input type="file" ref={fileRef} onChange={handleFileUpload} style={{ display: 'none' }} accept={FILE_ACCEPT} />
              <button className="btn-ghost" onClick={() => fileRef.current.click()} disabled={uploading}>
                {uploading ? '...' : '📎'}
              </button>
              <button className="btn-primary" onClick={sendMessage} disabled={sending || (!msgText.trim() && msgFiles.length === 0)}>
                {sending ? '...' : 'Отправить'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FilePreview({ file, onOpen }) {
  const openFile = async () => {
    if (typeof file === 'string') {
      const fullUrl = file.startsWith('http') ? file : `${BASE_URL}${file}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const res = await api.get(`/api/files/${file.id}/link`);
    window.open(res.data.url, '_blank', 'noopener,noreferrer');
  };

  const name = fileName(file);
  const isLegacyImage = typeof file === 'string' && /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
  if (isLegacyImage) {
    const fullUrl = file.startsWith('http') ? file : `${BASE_URL}${file}`;
    return <a href={fullUrl} target="_blank" rel="noreferrer"><img src={fullUrl} alt="" className="file-thumb" /></a>;
  }

  return <button type="button" onClick={openFile} className="file-link">📎 {name}</button>;
}


function SecureFilePreview({ file, onOpen }) {
  const name = fileName(file);
  const isLegacyImage = typeof file === 'string' && /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
  if (isLegacyImage) {
    const fullUrl = file.startsWith('http') ? file : `${BASE_URL}${file}`;
    return <a href={fullUrl} target="_blank" rel="noreferrer"><img src={fullUrl} alt="" className="file-thumb" /></a>;
  }

  const icon = isPreviewable(file) ? '🖼️' : '📎';
  return <button type="button" onClick={() => onOpen(file)} className="file-link">{icon} {name}</button>;
}

function FilePreviewModal({ preview, onClose }) {
  const isImage = /^image\//.test(preview.type) || /\.(jpg|jpeg|png|gif|webp)$/i.test(preview.name);
  const isPdf = preview.type === 'application/pdf' || /\.pdf$/i.test(preview.name);

  return (
    <div className="file-modal-backdrop" onClick={onClose}>
      <div className="file-modal" onClick={e => e.stopPropagation()}>
        <div className="file-modal-header">
          <span>{preview.name}</span>
          <button type="button" onClick={onClose} aria-label="Закрыть">×</button>
        </div>
        <div className="file-modal-body">
          {isImage && <img src={preview.objectUrl} alt={preview.name} />}
          {isPdf && <iframe src={preview.objectUrl} title={preview.name} />}
          {!isImage && !isPdf && <a href={preview.objectUrl} target="_blank" rel="noreferrer">Open file</a>}
        </div>
      </div>
    </div>
  );
}
